import { useEnvs, useMainStore } from '#imports';
import { useWeb3ModalAccount } from '@web3modal/ethers/vue';
import { parseUnits } from 'ethers';
import {
  BlackPearlVesting__factory,
  Dex__factory,
  Erc20Token__factory,
  GoldYield__factory,
  PearlStore__factory,
  PearlYield__factory
} from 'public/abi/evm/types';
import useProvider from '~/composables/useProvider';
import type { ContractCallParams } from '~/types/Web3Provider';
import { Tokens, tokensConfig } from '~/utils/constants';
import useSendContractMethod from './useSendContractMethod';

type Web3Confirmation = (confirmations?: number) => void;

export default () => {
  const { address } = useWeb3ModalAccount();
  const { sendContractMethod } = useSendContractMethod();
  const { blockchain } = useEnvs();
  const store = useMainStore();
  const { getProvider, getSigner } = useProvider();

  const sendContractMethodExtended = async <T extends Record<any, any>, M extends keyof T>(
    sendParams: ContractCallParams<T, M>,
    onConfirmation?: (confirmations?: number) => void
  ) => {
    const from = address.value;
    return sendContractMethod({ ...sendParams, from }, onConfirmation);
  };

  const createErc20 = async (address: string) => {
    return Erc20Token__factory.connect(address, await getSigner());
  };

  const approve = async (
    token: string,
    quantity: number,
    onConfirmation?: Web3Confirmation,
    storeTokenName?: Tokens
  ) => {
    const contractAddr = blockchain.contracts[storeTokenName || store.currentToken].addresses;
    const storeOrMine = storeTokenName === Tokens.pearl ? contractAddr.store : contractAddr.mine;
    return sendContractMethodExtended(
      {
        contract: await createErc20(token),
        methodName: 'approve',
        args: [storeOrMine || '', parseUnits(quantity.toString(), 'ether')]
      },
      onConfirmation
    );
  };

  const approveSpendUsdt = async (quantity: number | string, contractAddress: string) => {
    return sendContractMethodExtended({
      contract: await createErc20(blockchain.contracts.usdt),
      methodName: 'approve',
      args: [contractAddress, parseUnits(quantity.toString(), 'ether')]
    });
  };

  const approveDex = async (amount: string, tokenAddress: string) => {
    return sendContractMethodExtended({
      contract: await createErc20(tokenAddress),
      methodName: 'approve',
      args: [blockchain.contracts.dex, parseUnits(amount, 'ether')]
    });
  };

  /**
   * buyPearlPack
   * @param value - human-readable value like 0.1 USDT
   * @param tokenName
   * @param onConfirmation - confirmation callback, fired on each head block
   */
  const buyPack = async (value: string, tokenName: Tokens, onConfirmation?: Web3Confirmation) => {
    const amount = parseUnits(value, 'ether');

    return sendContractMethodExtended(
      {
        contract: PearlStore__factory.connect(blockchain.contracts[tokenName].addresses.store, await getSigner()),
        methodName: `buyPearlPack`,
        args: [amount]
      },
      onConfirmation
    );
  };

  const buyPackAndRegisterInReferralProgram = async (
    value: string,
    tokenName: Tokens,
    referrerAddress: string,
    onConfirmation?: Web3Confirmation
  ) => {
    const amount = parseUnits(value, 'ether');

    const storeContract = PearlStore__factory.connect(blockchain.contracts[tokenName].addresses.store, getProvider());
    const iface = storeContract.interface;

    return sendContractMethodExtended(
      {
        contract: PearlStore__factory.connect(blockchain.contracts.pearl.addresses.store, await getSigner()),
        methodName: 'multicall',
        args: [
          [
            iface?.encodeFunctionData('registerReferral', [referrerAddress]),
            iface?.encodeFunctionData(`buyPearlPack`, [amount])
          ]
        ]
      },
      onConfirmation
    );
  };

  const registerInReferral = async (referrerAddress: string, onConfirmation?: Web3Confirmation) => {
    return sendContractMethodExtended(
      {
        contract: PearlStore__factory.connect(blockchain.contracts.pearl.addresses.store, await getSigner()),
        methodName: 'registerReferral',
        args: [referrerAddress]
      },
      onConfirmation
    );
  };

  const claimReward = async (onConfirmation?: Web3Confirmation, tokenName: Tokens = store.currentToken) => {
    return sendContractMethodExtended(
      {
        contract: (tokenName === Tokens.gold ? GoldYield__factory : PearlYield__factory).connect(
          blockchain.contracts[tokenName].addresses.yield,
          await getSigner()
        ),
        methodName: 'claim',
        args: []
      },
      onConfirmation
    );
  };

  const unlock = async (tokenName: string, index: number) => {
    if (tokenName === tokensConfig[store.currentToken].name.contract) {
      const factory = tokenName === Tokens.gold ? GoldYield__factory : PearlYield__factory;

      return factory.connect(blockchain.contracts[store.currentToken].addresses.yield, await getSigner()).unlock(index);
    } else if (tokenName === tokensConfig[Tokens.blackPearl].name.contract) {
      return BlackPearlVesting__factory.connect(
        blockchain.contracts.blackPearlVesting,
        await getSigner()
      ).withdrawAvailable(index);
    }

    throw new Error('Invalid token name');
  };

  const swapExactTokensForTokens = async (
    amountIn: string,
    amountOut: string,
    tokenIn: string,
    tokenOut: string,
    address: string,
    deadline: number
  ) => {
    const amountInDecimal = parseUnits(amountIn, 18);
    const amountOutDecimal = parseUnits(amountOut, 18);

    return await sendContractMethod({
      contract: Dex__factory.connect(blockchain.contracts.dex, await getSigner()),
      methodName: 'swapExactTokensForTokens',
      args: [amountInDecimal, amountOutDecimal, [tokenIn, tokenOut], address, deadline]
    });
  };

  return {
    approve,
    buyPack,
    claimReward,
    unlock,
    buyPackAndRegisterInReferralProgram,
    registerInReferral,
    approveSpendUsdt,
    approveDex,
    swapExactTokensForTokens
  };
};
